import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useAppDispatch, useAppSelector } from "../../store";
import { getHunterCompanyThunk, getHunterEmployeeThunk } from "../../store/actions/admin.action";
import { HunterCompanyDto } from "../../models/admin.model";

type RawEmployee = {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  hadEmail: boolean;
  position: string;
  source: string;
  department: string;
};

type EmployeeWithRole = RawEmployee & {
  role: string;
};

const Companies: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<HunterCompanyDto | null>(null);
  const dispatch = useAppDispatch();
  const { hunterCompanyData, loading } = useAppSelector(
    (state) => state.adminReducer
  );
  const [hunterEmployeeData, setHunterEmployeeData] = useState<EmployeeWithRole[]>([]);
  const [selectedEmployeeEmails, setSelectedEmployeeEmails] = useState<string[]>([]);

  const fetchCompanies = async () => {
    try {
      const res = await dispatch(getHunterCompanyThunk(searchQuery)).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCompanyDetails = async (company: HunterCompanyDto) => {
    try {
      const res = await dispatch(getHunterEmployeeThunk(company.domainName)).unwrap();
      console.log(res);
      const employees: EmployeeWithRole[] = Object.entries(res.data).flatMap(
        ([role, employeeList]) =>
          employeeList.map((employee: RawEmployee) => ({
            ...employee,
            role,
          }))
      );
      setHunterEmployeeData(employees);
    } catch (error) {
      console.error(error);
    }
  };

  const handleEmailClick = (email: string) => {
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&bcc=${encodeURIComponent(
      email
    )}`;
    window.open(gmailUrl, "_blank");
  };

  const handleBackToCompanies = () => {
    setSelectedCompany(null);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      setHasSearched(true);
      fetchCompanies();
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedEmployeeEmails(hunterEmployeeData.map(emp => emp.email));
    } else {
      setSelectedEmployeeEmails([]);
    }
  };

  const handleSelectEmployee = (email: string) => {
    setSelectedEmployeeEmails(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const handleContactAll = () => {
    if (selectedEmployeeEmails.length === 0) return;
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&bcc=${encodeURIComponent(selectedEmployeeEmails.join(","))}`;
    window.open(gmailUrl, "_blank");
  };

  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyDetails(selectedCompany);
    }
  }, [selectedCompany]);

  return (
    <Container isSearching={hasSearched}>
      {!selectedCompany ? (
        <>
          {/* Initial view with centered search */}
          <InitialView isSearching={hasSearched}>
            <Title isSearching={hasSearched}>Discover Companies</Title>
            <Description isSearching={hasSearched}>
              Search and explore companies to support marketing efforts.
            </Description>
            <CenteredSearchContainer isSearching={hasSearched}>
              <SearchInput
                type="text"
                placeholder="Search companies, industries, or locations..."
                value={searchQuery}
                onChange={handleSearch}
                onKeyDown={handleKeyPress}
              />
              <SearchIcon>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                    stroke="#FF8E53"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </SearchIcon>
            </CenteredSearchContainer>
          </InitialView>

          {/* Search results view */}
          {hasSearched && (
            <ResultsContainer isSearching={hasSearched}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Image</TableHeader>
                    <TableHeader>Company Name</TableHeader>
                    <TableHeader>Domain Name</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {hunterCompanyData.length > 0 &&
                    !loading &&
                    hunterCompanyData.map(
                      (company: HunterCompanyDto, index: number) => (
                        <TableRowContent
                          key={index}
                          onClick={() => setSelectedCompany(company)}
                        >
                          <TableCell>
                            <CompanyImage
                              src={company.imageUrl}
                              alt={company.companyName}
                            />
                          </TableCell>
                          <TableCell>
                            <CompanyName>{company.companyName}</CompanyName>
                          </TableCell>
                          <TableCell>{company.domainName}</TableCell>
                        </TableRowContent>
                      )
                    )}
                  {hunterCompanyData.length === 0 && !loading && (
                    <TableRowContent>
                      <TableCell colSpan={3}>No companies found</TableCell>
                    </TableRowContent>
                  )}
                  {loading && (
                    <TableRowContent>
                      <TableCell colSpan={3}>Loading...</TableCell>
                    </TableRowContent>
                  )}
                </tbody>
              </Table>
            </ResultsContainer>
          )}
        </>
      ) : (
        // Company Details View
        <CompanyDetailsContainer>
          <BackButton onClick={handleBackToCompanies}>
            <BackIcon>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 12H5M12 19L5 12L12 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </BackIcon>
            Back to Companies
          </BackButton>

          <CompanyHeader>
            <CompanyImage
              src={selectedCompany.imageUrl}
              alt={selectedCompany.companyName}
            />
            <CompanyTitle>{selectedCompany.companyName}</CompanyTitle>
          </CompanyHeader>

          <CompanyInfo>
            <InfoItem>
              <InfoLabel>Domain Name</InfoLabel>
              <InfoValue>{selectedCompany.domainName}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Number of Emails</InfoLabel>
              <InfoValue>{selectedCompany.totalEmails || 0}</InfoValue>
            </InfoItem>
          </CompanyInfo>

          <EmployeesHeaderRow>
            <SectionTitle>Employees</SectionTitle>
            {selectedEmployeeEmails.length > 0 && (
              <ContactAllButton onClick={handleContactAll}>
                <ContactIcon>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 6L12 13L2 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ContactIcon>
                Contact All
              </ContactAllButton>
            )}
          </EmployeesHeaderRow>
          <EmployeeTable>
            <thead>
              <tr>
                {/* Select All Checkbox */}
                <EmployeeTableHeader>
                  <input
                    type="checkbox"
                    checked={hunterEmployeeData.length > 0 && selectedEmployeeEmails.length === hunterEmployeeData.length}
                    onChange={handleSelectAll}
                    aria-label="Select all employees"
                  />
                </EmployeeTableHeader>
                <EmployeeTableHeader>Name</EmployeeTableHeader>
                <EmployeeTableHeader>Position</EmployeeTableHeader>
                <EmployeeTableHeader>Department</EmployeeTableHeader>
                <EmployeeTableHeader>Email</EmployeeTableHeader>
                <EmployeeTableHeader>Contact</EmployeeTableHeader>
              </tr>
            </thead>
            <tbody>
              {hunterEmployeeData.length > 0 && !loading && hunterEmployeeData.map((employee: EmployeeWithRole, index: number) => (
                <EmployeeRow key={index}>
                  {/* Per-row checkbox */}
                  <EmployeeTableCell>
                    <input
                      type="checkbox"
                      checked={selectedEmployeeEmails.includes(employee.email)}
                      onChange={() => handleSelectEmployee(employee.email)}
                      aria-label={`Select ${employee.fullName}`}
                    />
                  </EmployeeTableCell>
                  <EmployeeTableCell>{employee.fullName}</EmployeeTableCell>
                  <EmployeeTableCell>{employee.position}</EmployeeTableCell>
                  <EmployeeTableCell>{employee.department}</EmployeeTableCell>
                  <EmployeeTableCell>{employee.email}</EmployeeTableCell>
                  <EmployeeTableCell>
                    <ContactButton onClick={() => handleEmailClick(employee.email)}>
                      <ContactIcon>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </ContactIcon>
                      Contact
                    </ContactButton>
                  </EmployeeTableCell>
                </EmployeeRow>
              ))}
              {hunterEmployeeData.length === 0 && !loading && (
                <EmployeeRow>
                  <EmployeeTableCell colSpan={6}>No employees found</EmployeeTableCell>
                </EmployeeRow>
              )}
              {loading && (
                <EmployeeRow>
                  <EmployeeTableCell colSpan={6}>Loading...</EmployeeTableCell>
                </EmployeeRow>
              )}
            </tbody>
          </EmployeeTable>
        </CompanyDetailsContainer>
      )}
    </Container>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-200px);
  }
`;

const slideInFromBottom = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div<{ isSearching: boolean }>`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  animation: ${fadeIn} 0.3s ease-out;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  margin-top: ${(props) => (props.isSearching ? "0" : "100px")};
`;

const InitialView = styled.div<{ isSearching: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const Title = styled.h1<{ isSearching: boolean }>`
  color: #fff;
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 24px 0;
  background: linear-gradient(135deg, #ff8e53, #ff6b6b, #ff8e53);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: ${(props) => (props.isSearching ? "none" : "block")};
`;

const Description = styled.p<{ isSearching: boolean }>`
  color: rgba(255, 255, 255, 0.7);
  font-size: 20px;
  line-height: 1.6;
  margin: 0 0 48px 0;
  max-width: 600px;
  display: ${(props) => (props.isSearching ? "none" : "block")};
`;

const CenteredSearchContainer = styled.div<{ isSearching: boolean }>`
  position: relative;
  width: 100%;
  transition: all 0.5s ease-out;
  z-index: ${(props) => (props.isSearching ? "10" : "auto")};
`;

const ResultsContainer = styled.div<{ isSearching: boolean }>`
  animation: ${(props) => (props.isSearching ? slideInFromBottom : "none")} 0.5s
    ease-out 0.5s both;
  opacity: ${(props) => (props.isSearching ? "1" : "0")};
`;

const SearchInput = styled.input`
  width: 100%;
  height: 64px;
  background: rgba(45, 45, 50, 0.7);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 0 24px 0 64px;
  font-size: 18px;
  color: #fff;
  transition: all 0.3s ease;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #ff8e53;
    background: rgba(45, 45, 50, 0.9);
    box-shadow: 0 8px 32px #ff8f5314;
  }

  &:hover {
    border-color: #ff8f53ac;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  transition: color 0.3s ease;

  ${SearchInput}:focus + & {
    color: #6366f1;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 24px;
  background: rgba(45, 45, 50, 0.7);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
`;

const TableHead = styled.thead`
  background: rgba(30, 30, 35, 0.9);
`;

const TableRowContent = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;

  &:hover {
    background-color: rgba(241, 156, 99, 0.1);
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled.th`
  padding: 20px 24px;
  text-align: left;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableCell = styled.td`
  padding: 20px 24px;
  color: #9ca3af;
  font-size: 14px;
  transition: color 0.2s;
`;

const CompanyName = styled.div`
  color: #fff;
  font-weight: 500;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
`;

const CompanyImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 10px;
  object-fit: contain;
`;

const CompanyInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
`;

const InfoItem = styled.div`
  background: rgba(30, 30, 35, 0.5);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
`;

const InfoLabel = styled.div`
  color: #9ca3af;
  font-size: 12px;
  text-transform: uppercase;
  margin-bottom: 8px;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  color: #fff;
  font-size: 16px;
  font-weight: 500;
`;

const SectionTitle = styled.h3`
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 20px;
    background: linear-gradient(180deg, #ff6b6b, #ff8e53);
    border-radius: 2px;
  }
`;

const EmployeeTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 16px;
  background: rgba(30, 30, 35, 0.5);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const EmployeeTableHeader = styled.th`
  padding: 16px 20px;
  text-align: left;
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(45, 45, 50, 0.7);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const EmployeeTableCell = styled.td`
  padding: 16px 20px;
  color: #9ca3af;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: color 0.2s;

  &:first-child {
    color: #fff;
    font-weight: 500;
  }
`;

const EmployeeRow = styled.tr`
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(241, 156, 99, 0.1);

    ${EmployeeTableCell} {
      color: #fff;
    }
  }
`;

const ContactButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #9ca3af;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
  }
`;

const ContactIcon = styled.div`
  width: 16px;
  height: 16px;
  color: #9ca3af;
  transition: color 0.2s;

  ${ContactButton}:hover & {
    color: #fff;
  }
`;

const CompanyDetailsContainer = styled.div`
  max-width: 1200px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const BackButton = styled.div`
  border: none;
  color: #9ca3af;
  font-size: 16px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  margin-bottom: 24px;

  &:hover {
    color: #ff8e53;
  }
`;

const BackIcon = styled.div`
  width: 20px;
  height: 20px;
  color: #9ca3af;
  transition: color 0.2s;

  ${BackButton}:hover & {
    color: #ff8e53;
  }
`;

const CompanyHeader = styled.div`
  margin-bottom: 32px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 20px;
`;

const CompanyTitle = styled.h2`
  color: #fff;
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  background: linear-gradient(135deg, #ff8e53, #ff6b6b, #ff8e53);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ContactAllButton = styled.button`
  background: linear-gradient(90deg, #ff8e53, #ff6b6b);
  border: none;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
  padding: 10px 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(255, 110, 83, 0.08);
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: linear-gradient(90deg, #ff6b6b, #ff8e53);
    color: #fff;
  }
`;

const EmployeesHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export default Companies;
